import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { HelmetProvider } from "react-helmet-async";

// Contexts
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { SectionsProvider, useSections } from "@/contexts/SectionsContext";
import { KidsFriendlyProvider } from "@/contexts/KidsFriendlyContext";

// Components
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";

// Pages
import HomePage from "@/pages/HomePage";
import ExplorePage from "@/pages/ExplorePage";
import PlaceDetailPage from "@/pages/PlaceDetailPage";
import MapPage from "@/pages/MapPage";
import WeatherPage from "@/pages/WeatherPage";
import FavoritesPage from "@/pages/FavoritesPage";
import AuthPage from "@/pages/AuthPage";
import AdminPage from "@/pages/AdminPage";
import ItinerariesPage from "@/pages/ItinerariesPage";
import ItineraryDetailPage from "@/pages/ItineraryDetailPage";
import BookingPage from "@/pages/BookingPage";
import InfoPage from "@/pages/InfoPage";
import MorePage from "@/pages/MorePage";
import ArticleDetailPage from "@/pages/ArticleDetailPage";
import EventsPage from "@/pages/EventsPage";
import TrailsPage from "@/pages/TrailsPage";
import TrailDetailPage from "@/pages/TrailDetailPage";

// Seed database on first load
import { api } from "@/lib/api";
import AuthCallback from "@/components/AuthCallback";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const AppLayout = ({ children }) => {
  const { pathname } = useLocation();
  const hideNav = pathname === '/auth' || pathname.startsWith('/admin');
  
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {!hideNav && <Header />}
      <main className={hideNav || pathname === '/map' ? '' : 'pb-14'}>{children}</main>
      {!hideNav && <BottomNav />}
    </div>
  );
};

const SectionGuard = ({ sectionKey, children }) => {
  const { isSectionVisible } = useSections();
  const { user } = useAuth();
  const isAdmin = user?.is_admin;

  if (!isAdmin && !isSectionVisible(sectionKey)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const routeLocation = useLocation();

  useEffect(() => {
    api.seedDatabase().catch(() => {});
  }, []);

  // CRITICAL: Detect session_id from OAuth callback synchronously before rendering routes
  if (routeLocation.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <AppLayout>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/place/:id" element={<PlaceDetailPage />} />
        <Route path="/map" element={<SectionGuard sectionKey="map"><MapPage /></SectionGuard>} />
        <Route path="/weather" element={<SectionGuard sectionKey="weather"><WeatherPage /></SectionGuard>} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/itineraries" element={<SectionGuard sectionKey="itineraries"><ItinerariesPage /></SectionGuard>} />
        <Route path="/itinerary/:id" element={<SectionGuard sectionKey="itineraries"><ItineraryDetailPage /></SectionGuard>} />
        <Route path="/booking" element={<SectionGuard sectionKey="booking"><BookingPage /></SectionGuard>} />
        <Route path="/info" element={<InfoPage />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/article/:id" element={<ArticleDetailPage />} />
        <Route path="/events" element={<SectionGuard sectionKey="events"><EventsPage /></SectionGuard>} />
        <Route path="/trails" element={<SectionGuard sectionKey="trails"><TrailsPage /></SectionGuard>} />
        <Route path="/trails/:id" element={<SectionGuard sectionKey="trails"><TrailDetailPage /></SectionGuard>} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </AppLayout>
  );
};

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <LocationProvider>
            <SectionsProvider>
              <KidsFriendlyProvider>
                <AppRoutes />
                <Toaster 
                position="top-center" 
                toastOptions={{
                  style: {
                    background: '#1D1D1F',
                    color: '#fff',
                    borderRadius: '12px',
                  },
                }}
              />
              </KidsFriendlyProvider>
            </SectionsProvider>
          </LocationProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
